/**
 * Curated third-party "partner" catalog for the `blocked_partners` variant
 * delta (spec: plans/2.0.0/comparisons/08-block-axis.md §5, framing per
 * 09-variants.md).
 *
 * This drives the two-tier block set: users toggle recognised *partners*
 * (grouped by category, with a safe baked-in default) and, for the long
 * tail, add free-form *domains*. A stored `BlockSelection` resolves here to
 * the flat `blocked_url_patterns` the runner hands to Lighthouse.
 *
 * We deliberately hand-maintain a compact catalog of the common,
 * high-impact third parties rather than bundling third-party-web's full
 * dataset (274 KB) into Workers. The audit's detected third parties drive
 * which partners the dashboard shows; this catalog supplies the domain
 * patterns + the default policy. Custom domains cover anything not listed.
 */

export type PartnerCategory =
  | 'analytics'
  | 'tag-manager'
  | 'advertising'
  | 'social'
  | 'marketing'
  | 'customer-success'
  // Never auto-blocked — removing these makes the audit misleading.
  | 'cdn'
  | 'consent'
  | 'payment'
  | 'fonts';

export interface Partner {
  /** Stable key used in stored selections. */
  key: string;
  /** Human-readable name shown in the UI. */
  name: string;
  category: PartnerCategory;
  /** Hostnames (or host+path fragments) this partner serves from. */
  domains: string[];
}

/** Categories the curated default pre-selects — safe + meaningful to remove. */
export const DEFAULT_ON_CATEGORIES: readonly PartnerCategory[] = [
  'analytics',
  'tag-manager',
  'advertising',
  'social',
  'marketing',
  'customer-success',
];

/** Categories we never auto-block — blocking them makes the audit a lie. */
export const NEVER_BLOCK_CATEGORIES: readonly PartnerCategory[] = [
  'cdn',
  'consent',
  'payment',
  'fonts',
];

/** Human-readable category labels (single source — the dashboard reads these via the catalog endpoint). */
export const CATEGORY_LABELS: Record<PartnerCategory, string> = {
  analytics: 'Analytics',
  'tag-manager': 'Tag managers',
  advertising: 'Advertising',
  social: 'Social',
  marketing: 'Marketing',
  'customer-success': 'Chat / support',
  cdn: 'CDN',
  consent: 'Consent',
  payment: 'Payments',
  fonts: 'Fonts',
};

/** The common, high-impact third parties. Extend as needed. */
export const PARTNERS: readonly Partner[] = [
  {
    key: 'google-tag-manager',
    name: 'Google Tag Manager',
    category: 'tag-manager',
    domains: ['googletagmanager.com'],
  },
  {
    key: 'google-analytics',
    name: 'Google Analytics',
    category: 'analytics',
    domains: ['google-analytics.com', 'analytics.google.com', 'ssl.google-analytics.com'],
  },
  {
    key: 'google-ads',
    name: 'Google Ads / DoubleClick',
    category: 'advertising',
    domains: ['doubleclick.net', 'googleadservices.com', 'googlesyndication.com'],
  },
  {
    key: 'meta-pixel',
    name: 'Meta Pixel',
    category: 'advertising',
    domains: ['connect.facebook.net', 'facebook.com/tr'],
  },
  { key: 'hotjar', name: 'Hotjar', category: 'analytics', domains: ['hotjar.com', 'hotjar.io'] },
  {
    key: 'intercom',
    name: 'Intercom',
    category: 'customer-success',
    domains: ['intercom.io', 'intercomcdn.com', 'intercom.com'],
  },
  {
    key: 'segment',
    name: 'Segment',
    category: 'analytics',
    domains: ['segment.com', 'segment.io', 'cdn.segment.com'],
  },
  {
    key: 'hubspot',
    name: 'HubSpot',
    category: 'marketing',
    domains: ['hs-scripts.com', 'hs-analytics.net', 'hsforms.com'],
  },
  {
    key: 'linkedin-insight',
    name: 'LinkedIn Insight',
    category: 'advertising',
    domains: ['snap.licdn.com', 'px.ads.linkedin.com'],
  },
  {
    key: 'tiktok-pixel',
    name: 'TikTok Pixel',
    category: 'advertising',
    domains: ['analytics.tiktok.com'],
  },
  {
    key: 'x-ads',
    name: 'X / Twitter Ads',
    category: 'social',
    domains: ['static.ads-twitter.com', 'analytics.twitter.com'],
  },
  {
    key: 'mixpanel',
    name: 'Mixpanel',
    category: 'analytics',
    domains: ['mixpanel.com', 'cdn.mxpnl.com'],
  },
  {
    key: 'amplitude',
    name: 'Amplitude',
    category: 'analytics',
    domains: ['amplitude.com', 'api.amplitude.com', 'cdn.amplitude.com'],
  },
  {
    key: 'fullstory',
    name: 'FullStory',
    category: 'analytics',
    domains: ['fullstory.com', 'fs.fullstory.com'],
  },
  {
    key: 'zendesk',
    name: 'Zendesk',
    category: 'customer-success',
    domains: ['zendesk.com', 'zdassets.com', 'zopim.com'],
  },
  {
    key: 'drift',
    name: 'Drift',
    category: 'customer-success',
    domains: ['drift.com', 'driftt.com'],
  },
];

const PARTNERS_BY_KEY = new Map(PARTNERS.map((p) => [p.key, p]));

export interface BlockSelection {
  /** Categories toggled on. */
  categories?: PartnerCategory[];
  /** Per-partner overrides by key: force 'block' or 'allow' regardless of category. */
  partner_overrides?: Record<string, 'block' | 'allow'>;
  /** Free-form host patterns (advanced tier). */
  custom_domains?: string[];
}

/** A domain → a Lighthouse `blockedUrlPatterns` glob (substring match). */
export function domainToPattern(domain: string): string {
  const trimmed = domain.trim().replace(/^[*.]+/, '');
  return `*${trimmed}*`;
}

/** The default selection: the safe-to-remove categories, no overrides. */
export function defaultBlockSelection(): BlockSelection {
  return { categories: [...DEFAULT_ON_CATEGORIES], partner_overrides: {}, custom_domains: [] };
}

/** Partners a selection resolves to (category membership + per-partner overrides). */
export function selectedPartners(selection: BlockSelection): Partner[] {
  const cats = new Set(selection.categories ?? []);
  const overrides = selection.partner_overrides ?? {};
  const neverBlock = new Set(NEVER_BLOCK_CATEGORIES);
  return PARTNERS.filter((p) => {
    const override = overrides[p.key];
    if (override === 'block') return true;
    if (override === 'allow') return false;
    return cats.has(p.category) && !neverBlock.has(p.category);
  });
}

/** Expand a stored selection into deduped Lighthouse `blockedUrlPatterns`. */
export function resolveBlockedUrlPatterns(selection: BlockSelection): string[] {
  const patterns = new Set<string>();
  for (const partner of selectedPartners(selection)) {
    for (const domain of partner.domains) patterns.add(domainToPattern(domain));
  }
  for (const raw of selection.custom_domains ?? []) {
    const domain = raw.trim();
    if (domain) patterns.add(domain.includes('*') ? domain : domainToPattern(domain));
  }
  // Match the BlockedUrlPatternsSchema cap so resolution can't produce an
  // over-long list that the SnapshotJob boundary would reject.
  return [...patterns].slice(0, 100);
}

/** Look up a partner by key (for display / validation). */
export function partnerByKey(key: string): Partner | undefined {
  return PARTNERS_BY_KEY.get(key);
}
